import { BigFloat } from "bigfloat.js";
import type { MerchantBalance, ServiceFee } from "src/types";

function isNumeric(value: string): boolean {
	return /^-?\d+$/.test(value);
}

export function checkCorrectAmount(amount: string): boolean {
	if (!amount || amount[0] === "-") {
		return false;
	}

	const splitted = amount.split(".");

	if (splitted.length === 1) {
		return isNumeric(splitted[0]);
	}

	if (splitted.length !== 2) {
		return false;
	}

	return isNumeric(splitted[0]) && isNumeric(splitted[1]) && splitted[1] !== "";
}

export function normalizeAmount(
	inputAmount: string,
	maxBalance: string,
): string {
	const nullAmount = new BigFloat(0);
	const maxAmount = new BigFloat(maxBalance);
	const convertedAmount = new BigFloat(0).add(parseFloat(inputAmount));

	if (convertedAmount.lessThan(nullAmount)) {
		return nullAmount.toString();
	}
	if (convertedAmount.greaterThan(maxAmount)) {
		return maxAmount.toString();
	}

	return inputAmount;
}

export function renderTotalSum(
	amount: string,
	balance: MerchantBalance,
	fee: ServiceFee | undefined,
	deductFeeFromAmount = false,
): string {
	if (balance.amount === "empty" || !fee) {
		return "-";
	}

	// Deduct mode: merchant balance is debited exactly `amount`; the fee is
	// subtracted from what the recipient receives, not added on top.
	if (deductFeeFromAmount) {
		return new BigFloat(0).add(parseFloat(amount || "0")).toString();
	}

	const isSameCurrency = fee.currency === balance.ticker;
	const feeToAdd = isSameCurrency ? parseFloat(fee.currencyFee) : 0;

	const total = new BigFloat(0).add(parseFloat(amount || "0")).add(feeToAdd);

	return total.toString();
}

export function renderRecipientAmount(
	amount: string,
	balance: MerchantBalance,
	fee: ServiceFee | undefined,
): string {
	if (balance.amount === "empty" || !fee) {
		return "-";
	}

	const isSameCurrency = fee.currency === balance.ticker;
	const feeToSubtract = isSameCurrency ? parseFloat(fee.currencyFee) : 0;

	const net = new BigFloat(0).add(parseFloat(amount || "0")).sub(feeToSubtract);

	return net.toString();
}

export function renderTotalConvertedAmount(
	convertedAmount: string,
	fee: ServiceFee | undefined,
	deductFeeFromAmount = false,
): string {
	if (!convertedAmount || !fee) {
		return "-";
	}

	if (fee.isTest) {
		return "0";
	}

	if (deductFeeFromAmount) {
		return new BigFloat(0).add(parseFloat(convertedAmount)).toString();
	}

	const total = new BigFloat(0)
		.add(parseFloat(convertedAmount))
		.add(parseFloat(fee.usdFee));

	return total.toString();
}

export function getWithdrawalValidation(
	amount: string,
	balance: MerchantBalance,
	fee: ServiceFee | undefined,
	convertedAmount: string,
	deductFeeFromAmount = false,
) {
	const isValid = checkCorrectAmount(amount);
	const hasValidAmount = isValid && amount && parseFloat(amount) > 0;

	const totalSum = renderTotalSum(amount, balance, fee, deductFeeFromAmount);
	const hasInsufficientBalance =
		hasValidAmount && fee && parseFloat(totalSum) > parseFloat(balance.amount);

	// Min-withdrawal compares the USD value the recipient actually receives. In
	// deduct mode that's `convertedAmount - fee.usdFee`; otherwise it's `convertedAmount`.
	const usdForMinCheck =
		deductFeeFromAmount && fee
			? parseFloat(convertedAmount || "0") - parseFloat(fee.usdFee)
			: parseFloat(convertedAmount || "0");

	const hasAmountBelowMinimum =
		hasValidAmount &&
		convertedAmount &&
		usdForMinCheck < parseFloat(balance.minimalWithdrawalAmountUSD);

	// Deduct mode: the net (amount - serviceFee) must be positive.
	const deductNetNotPositive =
		hasValidAmount &&
		deductFeeFromAmount &&
		fee &&
		fee.currency === balance.ticker &&
		parseFloat(amount) <= parseFloat(fee.currencyFee);

	return {
		hasValidAmount,
		hasInsufficientBalance,
		hasAmountBelowMinimum,
		deductNetNotPositive,
	};
}
